import type { Condition } from 'Automations/nodeDefinitions/conditions'
import {
  hasPhaseDuration,
  isPhaseDurationOperator,
  isPhaseNameOperator,
  type PhaseDurationValue,
  readPhaseDurationValue,
  readPhaseNameValue
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/phase'

export const validatePhaseNameValue = ({ value }: { value: unknown }) => {
  const { operator, name } = readPhaseNameValue(value)
  if (!isPhaseNameOperator(operator)) {
    return incomplete('Operator required', 'operator')
  }
  if (operator !== 'NOT_SET' && !name?.trim()) {
    return incomplete('Name required', 'name')
  }
  return undefined
}

export const validatePhaseDurationValue = ({
  value,
  siblingConditions = []
}: {
  value: unknown
  siblingConditions?: Array<Condition>
}) => {
  const { operator, duration } = readPhaseDurationValue(value)
  if (!isPhaseDurationOperator(operator)) {
    return incomplete('Operator required', 'operator')
  }
  if (operator !== 'IS_OPEN_ENDED' && !hasPhaseDuration(duration)) {
    return incomplete('Duration required', 'duration')
  }
  if (operator === 'SHORTER_THAN' && duration) {
    const longerThanDuration = siblingConditions
      .filter(
        condition =>
          condition.property === 'PHASE' && condition.operator === 'DURATION'
      )
      .map(condition => readPhaseDurationValue(condition.value))
      .find(sibling => sibling.operator === 'LONGER_THAN')?.duration

    if (
      longerThanDuration &&
      !canDurationRangeMatch(longerThanDuration, duration)
    ) {
      return conflictingPhaseDurations()
    }
  }
  return undefined
}

const canDurationRangeMatch = (
  lowerDuration: PhaseDurationValue,
  upperDuration: PhaseDurationValue
): boolean => {
  const lowerMonths = durationMonths(lowerDuration)
  const upperMonths = durationMonths(upperDuration)

  if (lowerMonths === upperMonths) {
    return hasWholeDayStrictlyBetween(lowerDuration.days, upperDuration.days)
  }

  const lowerBounds = durationDayBounds(lowerDuration)
  const upperBounds = durationDayBounds(upperDuration)
  return hasWholeDayStrictlyBetween(lowerBounds.minimum, upperBounds.maximum)
}

const hasWholeDayStrictlyBetween = (
  lowerDays: number,
  upperDays: number
): boolean => lowerDays + 1 < upperDays

const durationMonths = ({ years, months }: PhaseDurationValue): number =>
  years * 12 + months

// A calendar month changes length with the quote start date. These bounds only
// reject a range when no whole-day duration can sit strictly between them.
const durationDayBounds = (duration: PhaseDurationValue) => {
  const totalMonths = durationMonths(duration)
  const completeYears = Math.floor(totalMonths / 12)
  const remainingMonths = totalMonths % 12

  return {
    minimum: completeYears * 365 + remainingMonths * 28 + duration.days,
    maximum: completeYears * 366 + remainingMonths * 31 + duration.days
  }
}

const conflictingPhaseDurations = () => ({
  title: 'Conflicting phase durations',
  message:
    "The 'shorter than' duration must be longer than the 'longer than' duration",
  metadata: { field: 'duration' }
})

const incomplete = (
  message: string,
  field: 'operator' | 'name' | 'duration'
) => ({
  title: 'Incomplete condition',
  message,
  metadata: { field }
})
